import {
  CloseOutlined,
  FullscreenOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import Image from 'next/image';
import { ReactNode, TouchEvent, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useSafeLayoutEffect } from '../../hooks/use-safe-layout-effect';
import { __DEV__ } from '../../utils';
import { Box } from '../box';
import { Flex } from '../flex';
import { IconButton } from '../icon-button';
import { Stack } from '../stack';

const Overlay = ({ children }: { children: ReactNode }) => {
  return (
    <RemoveScroll>
      <Box
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        right={0}
        bg="white"
        overflow="hidden"
        zIndex={1500}
      >
        {children}
      </Box>
    </RemoveScroll>
  );
};

type HeaderProps = {
  counter: string;
  current: string;
  onClose: () => void;
};

const Header = ({ onClose, counter, current }: HeaderProps) => {
  const onClick = () => {
    window.open(current, 'Kaaja');
  };

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      px={24}
      py={24}
      width="100%"
      position="fixed"
      top={0}
      zIndex={1}
    >
      <Box
        color="white"
        bg="rgba(19, 18, 35, 0.5)"
        px={3}
        py={2}
        borderRadius={4}
        fontSize={12}
      >
        {counter}
      </Box>

      <Stack gap={5}>
        <IconButton
          icon={FullscreenOutlined}
          size="xs"
          variant="tertiary"
          p={0}
          onClick={onClick}
          fontSize={24}
        />

        <IconButton
          icon={CloseOutlined}
          size="xs"
          variant="tertiary"
          p={0}
          onClick={onClose}
          fontSize={24}
        />
      </Stack>
    </Flex>
  );
};

type ArrowsProps = {
  onNext: () => void;
  onPrev: () => void;
};

const Arrows = ({ onNext, onPrev }: ArrowsProps) => {
  return (
    <Box
      display={['none', 'flex']}
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      right={0}
      margin="0 auto"
      px={36}
      alignItems="center"
      justifyContent="space-between"
    >
      <IconButton
        icon={LeftOutlined}
        variant="tertiary"
        fontSize={32}
        style={{ zIndex: 1 }}
        p={0}
        onClick={onPrev}
      />
      <IconButton
        icon={RightOutlined}
        variant="tertiary"
        p={0}
        fontSize={32}
        style={{ zIndex: 1 }}
        onClick={onNext}
      />
    </Box>
  );
};

interface LightboxProps {
  items: string[];
  isOpen: boolean;
  onClose: () => void;
  itemIndex: number;
}

const Lightbox = ({
  items = [],
  isOpen = false,
  itemIndex = 0,
  onClose,
}: LightboxProps) => {
  // Porkaround
  useSafeLayoutEffect(() => {
    setIdx(itemIndex);
  }, [itemIndex]);

  const minSwipeDistance = 50;

  const [idx, setIdx] = useState(itemIndex);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      isLeftSwipe ? handleNext() : handlePrev();
    }
  };

  const handleNext = () => {
    setIdx((current) => (current + 1) % items.length);
  };

  const handlePrev = () => {
    setIdx((current) =>
      current === 0 ? items.length - 1 : (current - 1) % items.length
    );
  };

  if (!isOpen || items.length < 1) {
    return null;
  }

  const counter = `${idx + 1} / ${items.length}`;

  return (
    <Overlay>
      <Header onClose={onClose} counter={counter} current={items[idx]} />
      <Box
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        position="relative"
      >
        <Box
          style={{ aspectRatio: '16 / 9' }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <Image
            src={items[idx]}
            alt="Lightbox Image"
            layout="fill"
            objectFit="contain"
          />
        </Box>
        <Arrows onNext={handleNext} onPrev={handlePrev} />
      </Box>
    </Overlay>
  );
};

if (__DEV__) {
  Lightbox.displayName = 'Lightbox';
}

export { Lightbox };
